import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchBlogsDetails,
  fetchProductComparison,
  fetchSearchProducts,
  fetchSoftwareCategories,
  fetchSoftwareDetails,
  fetchVendorCategories,
} from "./ungatedContentThunk";
import { GenericObjectInterface } from "@/src/utilities";
import { UngatedContentInterface } from "../interface/reducerInterface";

const initialState: UngatedContentInterface = {
  productCategories: [],
  categoriesInitialized: false,
  categoriesLoading: false,
  categoriesError: null,

  vendorCategories: [],
  vendorCategoriesInitialized: false,
  vendorCategoriesLoading: false,
  vendorCategoriesError: null,

  comparedProducts: {
    selectedCategories: null,
    selectedSubcategories: null,
    selectedForComparison: [],
    compareProductsLoading: false,
    comparisonLockedSubcategory: null,
  },

  blogFilters: {
    selectedBlogCategory: "Technology and IT",
    selectedBlogSubcategory: "IT Networking & Infrastructure",
  },

  vendorFilters: {
    selectedVendorCategory: "Technology and IT",
    selectedVendorSubcategory: "IT Networking & Infrastructure",
  },

  interviewPodcastFilters: {
    selectedInterviewPodcastCategory: null,
    selectedInterviewPodcastSubcategory: null,
  },

  whitepaperFilters: {
    selectedWhitepaperCategory: null,
    selectedWhitepaperSubcategory: null,
  },

  caseStudyFilters: {
    selectedCaseStudyCategory: null,
    selectedCaseStudySubcategory: null,
  },

  infographicsFilters: {
    selectedInfographicsCategory: null,
    selectedInfographicsSubcategory: null,
  },

  webinarFilters: {
    selectedWebinarCategory: null,
    selectedWebinarSubcategory: null,
  },

  newsFilters: {
    selectedNewsCategory: null,
    selectedNewsSubcategory: null,
  },

  blogDetails: {
    details: [],
    meta: {
      page: 0,
      pageCount: 0,
      pageSize: 0,
      total: 0,
    },
  },
  productSearch: [],

  userFeedback: {
    article: {},
    newsletter: {},
    faq: {},
    vendor: {},
    product: {},
  },
};

export const ungatedContentSlice = createSlice({
  name: "ungatedContent",
  initialState,
  reducers: {
    updateProductsSelectedForComparison: (state, action) => {
      const id = action.payload.documentId;
      if (!id) return;
      if (
        state.comparedProducts.selectedForComparison.find(
          (item) => item.documentId === id
        )
      ) {
        // remove if exists
        state.comparedProducts.selectedForComparison =
          state.comparedProducts.selectedForComparison.filter(
            (item) => item.documentId !== id
          );
      } else {
        console.log(
          state.comparedProducts.selectedForComparison,
          "state.comparedProducts.selectedForComparison"
        );

        // add if not exists and limit not exceeded
        if (state.comparedProducts.selectedForComparison.length < 4) {
          state.comparedProducts.selectedForComparison = [
            ...state.comparedProducts.selectedForComparison,
            action.payload,
          ];
        }
      }
    },

    updateSelectedCategories: (state, action) => {
      state.comparedProducts.selectedCategories = action.payload;
    },
    updateSelectedSubcategories: (state, action) => {
      state.comparedProducts.selectedSubcategories = action.payload;
    },
    updateComparisonLockedSubcategory: (state, action) => {
      state.comparedProducts.comparisonLockedSubcategory = action.payload;
    },
    updateBlogCategory: (state, action) => {
      state.blogFilters.selectedBlogCategory = action.payload;
    },
    updateBlogSubcategory: (state, action) => {
      state.blogFilters.selectedBlogSubcategory = action.payload;
    },
    updateVendorCategory: (state, action) => {
      state.vendorFilters.selectedVendorCategory = action.payload;
    },
    updateVendorSubcategory: (state, action) => {
      state.vendorFilters.selectedVendorSubcategory = action.payload;
    },
    updateInterviewPodcastCategory: (state, action) => {
      state.interviewPodcastFilters.selectedInterviewPodcastCategory =
        action.payload;
    },
    updateInterviewPodcastSubcategory: (state, action) => {
      state.interviewPodcastFilters.selectedInterviewPodcastSubcategory =
        action.payload;
    },
    updateWhitepaperCategory: (state, action) => {
      state.whitepaperFilters.selectedWhitepaperCategory = action.payload;
    },
    updateWhitepaperSubcategory: (state, action) => {
      state.whitepaperFilters.selectedWhitepaperSubcategory = action.payload;
    },
    updateCaseStudyCategory: (state, action) => {
      state.caseStudyFilters.selectedCaseStudyCategory = action.payload;
    },
    updateCaseStudySubcategory: (state, action) => {
      state.caseStudyFilters.selectedCaseStudySubcategory = action.payload;
    },
    updateInfographicsCategory: (state, action) => {
      state.infographicsFilters.selectedInfographicsCategory = action.payload;
    },
    updateInfographicsSubcategory: (state, action) => {
      state.infographicsFilters.selectedInfographicsSubcategory =
        action.payload;
    },
    updateWebinarCategory: (state, action) => {
      state.webinarFilters.selectedWebinarCategory = action.payload;
    },
    updateWebinarSubcategory: (state, action) => {
      state.webinarFilters.selectedWebinarSubcategory = action.payload;
    },
    updateNewsCategory: (state, action) => {
      if(state.newsFilters){
        state.newsFilters.selectedNewsCategory = action.payload;
      }else{
        state = {
          ...state,
          newsFilters: {
            selectedNewsCategory: action.payload,
          } as any,
        }
      }
    },
    updateNewsSubcategory: (state, action) => {
      if(state.newsFilters){
        state.newsFilters.selectedNewsSubcategory = action.payload;
      }else{
        state = {
          ...state,
          newsFilters: {
            selectedNewsSubcategory: action.payload,
          } as any,
        }
      }
    },
    setUserFeedback: (
      state,
      action: PayloadAction<{
        contentType: "article" | "newsletter" | "faq" | "vendor" | "product";
        documentId: string;
        vote: "helpful" | "unhelpful";
      }>
    ) => {
      const { contentType, documentId, vote } = action.payload;
      if (!state.userFeedback) {
        state.userFeedback = {
          article: {},
          newsletter: {},
          faq: {},
          vendor: {},
          product: {},
        };
      }
      state.userFeedback[contentType][documentId] = vote;
    },
    clearUserFeedback: (
      state,
      action: PayloadAction<{
        contentType: "article" | "newsletter" | "faq" | "vendor" | "product";
        documentId: string;
      }>
    ) => {
      const { contentType, documentId } = action.payload;
      if (state.userFeedback?.[contentType]?.[documentId]) {
        delete state.userFeedback[contentType][documentId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSoftwareCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
        console.log("[BlogsSection] Fetching software categories...");
      })
      .addCase(
        fetchSoftwareCategories.fulfilled,
        (state, action: PayloadAction<GenericObjectInterface>) => {
          state.categoriesLoading = false;
          console.log(
            "[BlogsSection] Categories API response:",
            action.payload
          );

          if (action.payload?.data && action.payload?.data?.length > 0) {
            state.productCategories = action.payload?.data;

            if (!state.categoriesInitialized) {
              let firstCategory = action.payload?.data[0];
              let firstSubcategory = firstCategory?.subcategories?.[0];

              if (firstCategory?.documentId) {
                state.comparedProducts.selectedCategories =
                  firstCategory.documentId;
                state.blogFilters.selectedBlogCategory =
                  firstCategory.documentId;
                state.interviewPodcastFilters.selectedInterviewPodcastCategory =
                  firstCategory.documentId;
                state.whitepaperFilters.selectedWhitepaperCategory =
                  firstCategory.documentId;
                state.caseStudyFilters.selectedCaseStudyCategory =
                  firstCategory.documentId;
                state.infographicsFilters.selectedInfographicsCategory =
                  firstCategory.documentId;
                state.webinarFilters.selectedWebinarCategory =
                  firstCategory.documentId;
                state.newsFilters.selectedNewsCategory =
                  firstCategory.documentId;
              }
              if (firstSubcategory && firstSubcategory?.documentId) {
                // Initially, select the very first subcategory which contains products for comparison
                // Fallback to first subcategory if none has products
                const productSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.product > 0
                ) || firstCategory?.subcategories?.[0];
                state.comparedProducts.selectedSubcategories =
                  productSubcategory?.documentId || null;

                // Initially, select the very first subcategory which contains articles
                // Fallback to first subcategory if none has articles
                const articleSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.article > 0
                ) || firstCategory?.subcategories?.[0];
                state.blogFilters.selectedBlogSubcategory =
                  articleSubcategory?.documentId || null;

                // Initially, select the very first subcategory which contains interviews and so on...
                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.["interview-podcast"] > 0
                );
                state.interviewPodcastFilters.selectedInterviewPodcastSubcategory =
                  firstSubcategory?.documentId || null;

                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.whitepapers > 0
                );
                state.whitepaperFilters.selectedWhitepaperSubcategory =
                  firstSubcategory?.documentId || null;

                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.["case-study"] > 0
                );
                state.caseStudyFilters.selectedCaseStudySubcategory =
                  firstSubcategory?.documentId || null;

                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.["infographics"] > 0
                );
                state.infographicsFilters.selectedInfographicsSubcategory =
                  firstSubcategory?.documentId || null;

                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.["webinar-videos"] > 0
                );
                state.webinarFilters.selectedWebinarSubcategory =
                  firstSubcategory?.documentId || null;

                firstSubcategory = firstCategory?.subcategories?.find(
                  (subCat: GenericObjectInterface) =>
                    subCat?.contentCount?.["rss-feed"] > 0
                );
                state.newsFilters.selectedNewsSubcategory =
                  firstSubcategory?.documentId || null;
              }

              state.categoriesInitialized = true;
            }
          } else {
            console.warn(
              "[BlogsSection] Categories API returned empty or invalid data:",
              action.payload
            );
            state.categoriesError = "No categories found";
          }
        }
      )
      .addCase(fetchSoftwareCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError =
          action.error.message || "Failed to load categories";
        console.error(
          "[BlogsSection] Failed to fetch categories:",
          action.error
        );
      });

    builder
      .addCase(fetchVendorCategories.pending, (state) => {
        state.vendorCategoriesLoading = true;
        state.vendorCategoriesError = null;
        console.log("[VendorSection] Fetching vendor categories...");
      })
      .addCase(
        fetchVendorCategories.fulfilled,
        (state, action: PayloadAction<GenericObjectInterface>) => {
          state.vendorCategoriesLoading = false;

          if (action.payload?.data && action.payload?.data?.length > 0) {
            state.vendorCategories = action.payload?.data;

            if (!state.vendorCategoriesInitialized) {
              // const firstCategory = action.payload?.data[0];
              let firstCategory = action.payload?.data?.find(
                (cat: GenericObjectInterface) =>
                  cat?.subcategories?.some((subCat: GenericObjectInterface) =>
                    Object.values(subCat?.contentCount)?.some(
                      (el: any) => el > 0
                    )
                  )
              );
              const firstSubcategory = firstCategory?.subcategories?.find(
                (subCat: GenericObjectInterface) =>
                  subCat?.contentCount?.vendor > 0
              );
              if (firstCategory?.documentId) {
                state.vendorFilters.selectedVendorCategory =
                  firstCategory.documentId;
              }

              if (firstSubcategory?.documentId) {
                state.vendorFilters.selectedVendorSubcategory =
                  firstSubcategory?.documentId || null;
              }

              state.vendorCategoriesInitialized = true;
            }
          } else {
            console.warn(
              "[VendorSection] Categories API returned empty or invalid data:",
              action.payload
            );
            state.vendorCategoriesError = "No categories found";
          }
        }
      )
      .addCase(fetchVendorCategories.rejected, (state, action) => {
        state.vendorCategoriesLoading = false;
        state.vendorCategoriesError =
          action.error.message || "Failed to load categories";
        console.error(
          "[VendorSection] Failed to fetch categories:",
          action.error
        );
      });

    builder
      .addCase(fetchProductComparison.pending, (state) => {
        state.comparedProducts.compareProductsLoading = true;
      })
      .addCase(
        fetchProductComparison.fulfilled,
        (state, action: PayloadAction<GenericObjectInterface>) => {
          state.comparedProducts.compareProductsLoading = false;
          if (action.payload?.data && action.payload?.data?.length > 0) {
            state.comparedProducts.selectedForComparison = action.payload?.data;
          }
        }
      );

    builder.addCase(
      fetchBlogsDetails.fulfilled,
      (state, action: PayloadAction<GenericObjectInterface>) => {
        if (action.payload?.data && action.payload?.data?.length > 0) {
          state.blogDetails.details = action.payload?.data;
          state.blogDetails.meta = action.payload?.meta?.pagination;
        }
      }
    );

    builder.addCase(
      fetchSearchProducts.fulfilled,
      (state, action: PayloadAction<GenericObjectInterface[]>) => {
        console.log(action.payload, "action.payload");
        if (action.payload && action.payload?.length > 0) {
          state.productSearch = action.payload;
        }
      }
    );
  },
});

// Action creators are generated for each case reducer function
export const {
  updateProductsSelectedForComparison,
  updateSelectedCategories,
  updateSelectedSubcategories,
  updateComparisonLockedSubcategory,
  updateBlogCategory,
  updateBlogSubcategory,
  updateVendorCategory,
  updateVendorSubcategory,
  updateInterviewPodcastCategory,
  updateInterviewPodcastSubcategory,
  updateWhitepaperCategory,
  updateWhitepaperSubcategory,
  updateCaseStudyCategory,
  updateCaseStudySubcategory,
  updateInfographicsCategory,
  updateInfographicsSubcategory,
  updateWebinarCategory,
  updateWebinarSubcategory,
  updateNewsCategory,
  updateNewsSubcategory,
  setUserFeedback,
  clearUserFeedback,
} = ungatedContentSlice.actions;

export default ungatedContentSlice.reducer;
